export type OrganizationMemberDetailsRouteParams = {
    id: string;
};

export type DeviceStatus =
    | 'ORDERED'
    | 'DELIVERED'
    | 'ACTIVATED'
    | 'SETUP_NEEDED'
    | 'CONFIGURED'
    | 'RETURN_NEEDED'
    | 'EXCHANGED'
    | 'CANCELLED'
    | 'None';

export type MemberRole =
    | 'PRIMARY_USER_ADMIN'
    | 'PRIMARY_USER_READ_ONLY'
    | 'CAREGIVER_ADMIN'
    | 'CAREGIVER_READ_ONLY';

export type MemberStatus = 'ENROLLED' | 'UNENROLLED' | 'ACTIVATED' | 'CANCELLED';

export type DisengagedReason =
    | 'EXCHANGED'
    | 'DEVICE_RESET'
    | 'NO_CONFIG'
    | 'NO_PILLS'
    | 'NO_SCHEDULES'
    | 'IN_VACATION'
    | 'TRANSPORT_MODE'
    | 'DISCONNECTED'
    | 'NO_ADHERENCE';

// Fleet member users are Caregivers and Org users combined, two different tables and that is why we have two ids
// hero_user_id -> caregiver if
// member_id -> org member id
export type FleetMemberIds = {
    hero_user_id: number | null; //  Unique ID for a given Hero User. Null in case when the device isn't paired.
    member_id: number | null; // Unique ID for a given member. Null in case when member details are for a Caregiver.
};

export type OrganizationMemberListDeviceInfo = {
    device_status: DeviceStatus | null;
    device_serial: string | null;
    device_status_timestamp: string | null;
    is_device_online: boolean;
    last_online: string | null;
};

export type ExchangeDeviceInfo = Pick<
    OrganizationMemberListDeviceInfo,
    'device_serial' | 'device_status' | 'is_device_online' | 'last_online'
>;

export type CaregiverInfo = {
    first_name: string | null;
    last_name: string | null;
    member_role: MemberRole | null;
    email: string | null;
    cell_phone: string | null;
} & FleetMemberIds;

export type StatusInfo = {
    status: 'ACTIVATED' | 'ENROLLED' | 'CANCELLED'; // [ENROLLED | ACTIVATED | CANCELLED],
    discharged_at: string | null; // [datetime | null]
    discharge_reason: string | null; // [str | null]
    discharge_reason_id: number | null; // [int | null]
    can_discharge: boolean; // bool
    can_readmit: boolean; // bool
    activated_at: string | null;
    cancelled_at: string | null;
    enrolled_at: string | null;
    can_re_enroll?: boolean;
    re_enroll_block_reason?: 'device_returned' | 'enrolled_elsewhere' | null;
    previous_admin_email?: string | null;
};

export type CmMonthlyCheckinStatus =
    | 'DUE'
    | 'NOT_DONE'
    | 'USER_CONFIRMED_CHECKIN'
    | 'COMPLETE'
    | 'DUE_LAST_DAY';

export type CmMonthlyCheckin = {
    status: CmMonthlyCheckinStatus;
    scheduled_date: string | null;
    checkin_date: string | null;
};

export type OrganizationMemberList = {
    first_name: string | null;
    last_name: string | null;
    // Name and DOB the member set on the device during first-run setup (FRE); display-only.
    // May differ from the form-filled first_name / last_name / date_of_birth above.
    // Optional: `enterprise/members` returns them, `enterprise/disengaged-members` does not.
    fre_first_name?: string | null;
    fre_last_name?: string | null;
    fre_dob?: string | null;
    activated_at: string | null;
    email: string | null;
    // Per-row org id (multi-org /members, HWM-22819); used to attribute + switch tenant on select.
    hero_organization_id?: string | null;
    // Returned on search results; used by the header search suggestions dropdown and by the
    // device name/DOB mismatch check.
    date_of_birth?: string | null;
    state?: string | null;
    external_user_id: string | null;
    readmitted_to: number | null;
    readmitted_from: number | null;
    status_info: StatusInfo | null;
    member_status: MemberStatus | null;
    member_role: MemberRole | null;
    device_info: OrganizationMemberListDeviceInfo | null;
    exchange_device_info: ExchangeDeviceInfo | null;
    caregivers: CaregiverInfo[] | null;
    care_manager_ids: number[] | null;
    // Null when the care-manager check-in feature is off for the org, or the member has no record.
    cm_monthly_checkin: CmMonthlyCheckin | null;
    disengaged_reason?: DisengagedReason;
    other_disengaged_reasons?: DisengagedReason[];
} & FleetMemberIds;

export type OrganizationMemberListPaginated = {
    offset: number;
    limit: number;
    total_count: number;
    list: OrganizationMemberList[];
};

/**
 * Fields the members list endpoint sorts by. Runtime tuple rather than a bare union so the URL
 * parser can reject a hand-edited `?sort_by=` before it reaches the backend, and `columns.ts` can
 * only mark a column sortable by a field the API actually supports.
 */
export const MEMBER_SORT_FIELDS = [
    'email',
    'first_name',
    'last_name',
    'activated_at',
    // Latest care-manager check-in scheduled date; costs the backend a conditional JOIN.
    'cm_checkin_schedule_date'
] as const;

export type MemberSortField = (typeof MEMBER_SORT_FIELDS)[number];

export type MemberSortOrder = 'asc' | 'desc';

/**
 * Latest care-manager check-in status to filter by. `due` covers DUE + DUE_LAST_DAY and
 * `completed` covers COMPLETE; either way the backend drops members with no check-in record, so
 * this narrows the list rather than just reordering it.
 */
export const CM_CHECKIN_STATUS_FILTERS = ['due', 'completed'] as const;

export type CmCheckinStatusFilter = (typeof CM_CHECKIN_STATUS_FILTERS)[number];

export type MemberGender = 'male' | 'female' | 'other' | 'prefer_not_to_say';

export type HealthConditionDetails = {
    id: string | null;
    alias: string | null;
};

export type OrgMemberLinkedMember = {
    member_role: MemberRole | null;
    first_name: string | null;
    last_name: string | null;
} & FleetMemberIds;

export type DeviceStatusList = {
    device_status: DeviceStatus;
    status_timestamp: string | null;

    // CANCELLED
    is_returned?: boolean;

    // ORDER
    fulfillment_details?: {
        delivered_at: string;
        delivery_expected_at: string;
        device_serial: string;
        fulfillment_request_status: 'FULFILLED';
        fulfillment_status: 'DELIVERED' | 'EXPORTED';
        tracking_number: string;
        tracking_url: string;
    }[];

    // EXCHANGE
    exchange_fulfillment_details?: {
        fulfillment_request_status: 'FULFILLED';
        fulfillment_status: 'DELIVERED' | 'EXPORTED';
        tracking_url: string;
        device_serial: string;
        tracking_number: string;
        delivery_expected_at: string;
        delivered_at: string;
    }[];
    return_tracking_number?: string;
    return_status?: string;
    return_serial?: string;
    return_label_url?: string;
};

export type DeviceInfo = {
    member_role: MemberRole | null;
    device_serial: string | null;
    online_status: {
        is_online: boolean | null;
        last_online: string | null;
    } | null;
    device_status: DeviceStatus | null;
    device_statuses: DeviceStatusList[] | null;
    tracking_number: string | null;
};

export type MedicationSchedule = {
    dow: string | null;
    time: string | null;
    every_x_days: number | null;
    next_date: string | null;
    qty: number | null;
    hero_schedule_id: number | null;
    valid_from: string | null;
    valid_to: string | null;
};

export type PillLevelEnum = 'empty' | 'low' | 'midlow' | 'mid' | 'high' | 'unknown' | 'ok';

export type MedicationDetails = {
    name: string | null;
    name_short?: string | null;
    last_refill_date_string?: string | null;
    pill_level_calculated?: string | null;
    estimated_amount?: number | null;
    hero_pill_id?: number | null;
    last_refill_qty?: number | null;
    dispense_count?: number | null;
    slot: number | null;
    pill_schedules: MedicationSchedule[] | null;
    expires: string | null;
    dosage: string | null;
    requires_pin: boolean;
    stored_in_hero: boolean;
    in_hero_from: string | null; //'2017-08-26';
    level_warning: PillLevelEnum | null; //'ok';
    pill_level_enum: PillLevelEnum | null;
    refill_prompt: boolean | null;
    update_exact_count?: boolean | null;
    update_expiration_date?: boolean | null;
    form: string | null;
    is_generic: boolean;
    gpi: string | null;
    pill_type: string | null;
    max_pills_per_dispense: number | null; //  [int | null]. Int in case of manual dispense.
    min_time_between_dispenses: number | null; //  [int | null]. Int in case of manual dispense (minutes).
    max_doses: number | null; //  [int | null]. Int in case of manual dispense. NOTE: if not null, manual dispenses are allowed!
    exact_pill_count: number | null; //  [int | null]
};

export type MembershipLog = {
    event:
        | 'undefined'
        | 'acknowledged'
        | 'vacation_started'
        | 'vacation_ended'
        | 'vacation_cancelled'
        | 'activated'
        | 'disengagement'
        | 're_engagement'
        | 'discharged'
        | 'readmitted'
        | 'monthly_call_reminder'
        | 'cancelled';
    timestamp: string | null;
    readmitted_at: string | null;
    user_start_datetime?: string | null;
    user_end_datetime?: string | null;
    device_end_datetime?: string | null;
    disengaged_reason: DisengagedReason | null;
    discharged_reason: string | null;
};

export type MemberInfo = {
    first_name: string | null;
    last_name: string | null;
    // Name and DOB the member set on the device during first-run setup (FRE); display-only.
    // May differ from the form-filled first_name / last_name / contact_info.date_of_birth.
    fre_first_name: string | null;
    fre_last_name: string | null;
    fre_dob: string | null;
    external_user_id: string | null;
    address_info: {
        address_line_1?: string | null;
        address_line_2?: string | null;
        city?: string | null;
        state?: string | null;
        zip?: string | null;
    } | null;
    contact_info: {
        email: string | null;
        phone_number: string | null;
        date_of_birth: string | null;
    };
    gender: MemberGender | null;
    health_conditions: HealthConditionDetails[] | null;
    organization_info: {
        organization_id: number | null;
        name: string | null;
        dealer_name: string | null;
    };
    status_info: StatusInfo | null;
    disengaged_reason: DisengagedReason | null;
    disengaged_reminders: { reminder_sent: string }[];
    can_send_disengaged_reminder: boolean;
    cohort_date: string;
    member_status: 'ACTIVATED';
    membership_logs: MembershipLog[];
    rtm_checkin: {
        status: 'due' | 'not_done' | 'user_confirmed_checkin' | 'complete';
        notification_phase:
            | 'first_month_primary'
            | 'first_month_secondary'
            | 'primary'
            | 'secondary';
        scheduled_check_in_date: string | null;
        next_checkin_time: string | null;
    } | null;
    care_manager_ids: number[] | null;
} & FleetMemberIds;

export type CaregivingInfo = {
    first_name: string | null;
    last_name: string | null;
    member_role: MemberRole | null; //  ["CAREGIVER_ADMIN" | "CAREGIVER_READ_ONLY"]
    device_info: {
        device_serial: string | null;
        online_status: {
            is_online: boolean | null;
            last_online: string | null;
        };
        device_status: DeviceStatus | null;
    };
} & FleetMemberIds;

export type RemovedMedication = {
    name: string | null;
    dosage: string | null;
};

export type DeviceLogEventType = 'OPEN_DOOR' | 'DEVICE_OFFLINE';

export type PastSevenDaysEvent = {
    event_type: DeviceLogEventType | null;
    event_count: number | null;
    timestamps?: string[];
};

export type VacationDetails = {
    device_configured_end_datetime: string;
    device_configured_start_datetime: string;
    dispense_status: 'undefined' | string;
    should_dispense: boolean;
    status: 'acknowledged' | 'vacation_cancelled' | 'vacation_started';
    user_configured_end_datetime: string;
    user_configured_start_datetime: string;
    user_timezone_name: string | null;
};

export type OrganizationMemberDetails = {
    member_info: MemberInfo;
    owned_device_info: DeviceInfo;
    medication_info: MedicationDetails[] | null;
    caregivers: CaregiverInfo[] | null;
    caregiving: CaregivingInfo[] | null;
    exchange_device_info: ExchangeDeviceInfo | null;
    removed_medication: RemovedMedication[] | null;
    device_log: {
        past_seven_days: PastSevenDaysEvent[] | null;
    };
    vacation_details?: VacationDetails;
    vacation_history?: VacationDetails[];
};

export type OrganizationDeviceStatistics = {
    online_count: number | null;
    offline_count: number | null;
    ordered_count: number | null;
    delivered_count: number | null;
    setup_needed_count: number | null;
};

export type AdherenceSummary = {
    positive: number;
    negative: number;
    unknown: number;
    positive_doses: number;
    negative_doses: number;
    unknown_doses: number;
    unscheduled_doses: number;
};

export type AdherencePeriods = {
    date_from: string;
    date_to: string;
    positive: number;
    negative: number;
    unknown: number;
}[];

export type MemberCreatePayload = {
    email: string;
    phone: string;
    caregiver_spoken?: string | undefined;
    first_name: string;
    last_name: string;
    role: string;
    is_member?: boolean;
    external_user_id?: string;
    shipping_address: {
        address_line_1: string;
        address_line_2?: string;
        city: string;
        zip_code: string;
        state: string;
    };
};

export type MemberUpdatePayload = {
    external_user_id?: string;
    contact: {
        // Optional
        first_name?: string;
        last_name?: string;
        date_of_birth?: string;
        email?: string;
        cell_phone?: string;
    };
    address: {
        address_line_1?: string;
        address_line_2?: string;
        city?: string;
        zip?: string;
        state?: string;
    };
};

export type PatientActivity = {
    id: number;
    created: string | null;
    activity: string | null;
    owner: string | null;
    duration: string | null;
    activity_status: string | null;
};

export type SelectedPatientActivity = {
    id: number;
    value: string;
    label: string;
    icon: JSX.Element;
};

export type DischargeReasons = {
    discharge_reasons: {
        group_title: string;
        reasons: {
            reason_id: number;
            human_readable: string;
        }[];
    }[];
};

export type MemberDischargePayload = {
    member_id: number;
    discharge_reason: number;
};

export type MemberReadmitPayload = {
    member_id: number;
};

export type MemberReadmitResponse = {
    member: {
        id: number;
        readmitted_to: number | null;
        hero_user_id: number | null;
    };
};

export type MemberReEnrollPayload = {
    member_id: number;
    admin_email: string;
};

export type MemberReEnrollResponse = {
    new_member_id: number;
};

export type MemberCheckinPayload = {
    hero_user_id: number;
    action: 'schedule_date' | 'complete';
    scheduled_date?: string; // format: 2025-11-22 08:30:00
};

export interface CheckinMembersResponse {
    members: CheckinMember[];
    limit: number;
    offset: number;
    total_count: number;
    is_checkin_enabled: boolean;
}

export interface CheckinMember {
    member: CheckinMemberDetails;
    care_manager_checkin: CheckinMemberCheckin;
}

export interface CheckinMemberDetails {
    id: number;
    first_name: string;
    last_name: string;
    date_of_birth: string | null;
    // Name and DOB the member set on the device during first-run setup (FRE); display-only.
    // Optional: not confirmed on `enterprise/checkin-members` yet.
    fre_first_name?: string | null;
    fre_last_name?: string | null;
    fre_dob?: string | null;
    email: string;
    hero_user_id: number;
    activated_at: string;
    external_user_id: any;
    care_manager_ids: number[];
}

export interface CheckinMemberCheckin {
    status: string;
    notification_phase: string;
    scheduled_check_in_date: string;
    next_checkin_time: string;
    show_prompt: boolean;
    is_auto_scheduled: boolean;
}

export interface AssignCareManagersPayload {
    care_manager_ids: number[];
    member_ids: number[];
}

export interface AssignCareManagersResponse {
    care_manager_ids: number[];
    member_ids: number[];
}

export interface UnassignCareManagersPayload {
    care_manager_ids: number[];
    member_ids: number[];
}

export interface UnassignCareManagersResponse {
    care_manager_ids: number[];
    member_ids: number[] | null;
    unassigned_all: boolean;
}

export interface CareManagerInfo {
    id: number;
    first_name: string;
    last_name: string;
    image_url: string | null;
}

export interface CareManager {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    image_url: string;
    hero_user_id: number;
    bio: string;
    specialty: string;
    capacity: string;
}

/**
 * The rtm-api clinical-data and custom-demographics endpoints wrap every payload in this
 * envelope. `useRequest` returns the raw body, so hooks type the envelope and unwrap it.
 */
export type ApiEnvelope<T> = {
    status: 'success' | 'error';
    data: T;
    message: string;
};

export type ApiEnvelopeError = { errors: string[] };

/** Hero-only supplemental demographics, stored per member and org — not tied to an EHR. */
export type CustomDemographics = {
    preferred_contact_method: string | null;
    preferred_contact_time: string | null;
    caregiver_first_name: string | null;
    caregiver_last_name: string | null;
    caregiver_phone: string | null;
    caregiver_email: string | null;
    caregiver_relationship: string | null;
    hero_ai_opt_in: boolean | null;
    device_pairing_status: string | null;
    notes: string | null;
};

/** PUT accepts any subset: absent fields are left untouched, `null` clears a field. */
export type CustomDemographicsPayload = Partial<CustomDemographics>;
